/*
 * Simple 4-Pack PDP main section (sections/p4-pdp-main.liquid). Quick add + reviews live in p4-pdp-extras.js.
 * - Designs: "variants" mode (one product, variants = designs) or "products" mode (one product per design).
 *   Switching is client-side: gallery (only that design's images), swatch, price, availability, ?variant= / URL.
 * - Design + interval pickers: accessible listbox (button + role=listbox, keyboard: arrows, Home/End,
 *   type-ahead, Esc), one shared implementation (listbox()).
 * - Selling plans: one entry per delivery interval, preselects the plan matching the "4 month" setting.
 * - Sticky Add to Bag bar: mobile only, shown / hidden by scroll position (initSticky).
 * - Add to Bag -> P4.addItems(...) then P4.packAdded(...) (4-packs only). Never opens the cart drawer itself.
 * Contract: docs/simple-4-pack/00-contract.md · Docs: docs/simple-4-pack/02-new-pdp.md
 * No globals besides window.P4Pdp.
 */
(function () {
  'use strict';

  if (window.P4Pdp) return;
  window.P4Pdp = { version: 2 };

  var IMG_WIDTHS = [360, 480, 600, 720, 860, 1000, 1100];
  var IMG_SIZES = '(min-width: 769px) 35vw, 76vw';

  function $(root, sel) {
    return root.querySelector(sel);
  }
  function $$(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }
  function money(cents) {
    if (window.P4 && typeof window.P4.formatMoney === 'function') return window.P4.formatMoney(cents);
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }
  function hasP4() {
    return !!(window.P4 && typeof window.P4.addItems === 'function' && typeof window.P4.packAdded === 'function');
  }
  function sizedUrl(src, w) {
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$1' + w);
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  /* ------------------------------------------------------------ Gallery */
  /* Image loading: the Liquid renders the 1st image eager (LCP), the 2nd + last (the peeking neighbours)
     native-lazy, and every other slide inside <noscript data-p4-defer> (or, for slides built here,
     an <img data-srcset> without src). Those are hydrated only once the shopper engages with the
     gallery (touch, pointer, wheel, keyboard, dots), two slides around the active one. */

  function hydrateSlide(el) {
    if (!el) return;
    $$(el, 'noscript[data-p4-defer]').forEach(function (ns) {
      var tpl = document.createElement('template');
      tpl.innerHTML = ns.textContent || '';
      ns.parentNode.replaceChild(tpl.content, ns);
    });
    $$(el, 'img[data-srcset]').forEach(function (img) {
      img.sizes = IMG_SIZES;
      img.srcset = img.getAttribute('data-srcset');
      img.src = img.getAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    });
  }

  /* Coverflow carousel (Oct 2026 mocks), transform-driven and circular. Nothing scrolls: every slide sits
     in the same grid cell and a class places it: is-active (centre card), is-prev / is-next (scaled-down
     peeks tucked under the centre card), is-before / is-after (hidden, off to the left / right). The CSS
     transitions only transform + opacity (plus the frame radius), so one click = one ~450 ms animation
     and nothing reads layout per frame. Dragging writes the same transform inline once per frame (rAF),
     then hands back to the classes, which animate from wherever the finger let go.
     Previous build (scroll-snap track + scroll-driven scale/translate): snap areas follow the transformed
     boxes, so every scroll frame moved the snap target the browser was snapping to (off by the 93 px
     tuck), and the per-frame "nearest" sync overrode the clicked target -> the left peek bounced. */
  var POS = ['is-before', 'is-prev', 'is-active', 'is-next', 'is-after'];

  function Gallery(el) {
    this.el = el;
    this.track = $(el, '[data-p4-track]');
    this.dotsWrap = $(el, '[data-p4-dots]');
    this.index = 0;
    this.engaged = false;
    this.drag = null;
    this.raf = 0;
    this.suppressClick = false;
    this.wheelAcc = 0;
    this.wheelT = 0;
    this.wheelLock = 0;
    this.lastPeek = null;
    if (!this.track) return;
    this.collect();
    this.place(0, 0);
    this.bind();
  }

  Gallery.prototype.bind = function () {
    var self = this;
    var track = this.track;
    track.addEventListener('click', function (e) {
      if (self.suppressClick) {
        self.suppressClick = false;
        e.preventDefault();
        return;
      }
      var s = e.target.closest && e.target.closest('[data-p4-slide]');
      if (!s) return;
      var now = Date.now();
      var lp = self.lastPeek;
      var d = s.classList.contains('is-prev') ? -1 : s.classList.contains('is-next') ? 1 : 0;
      // Quick repeated clicks on a peek: mid-animation the card sliding in is already is-active and sits
      // under the pointer, so a click on it within one transition continues in the same direction
      // (N quick clicks = N steps, never a back-flip).
      if (!d && lp && now - lp.t < 450) d = lp.dir;
      if (!d) return;
      self.lastPeek = { dir: d, t: now };
      self.step(d);
    });
    if (this.dotsWrap) {
      this.dotsWrap.addEventListener('click', function (e) {
        var dot = e.target.closest && e.target.closest('[data-p4-dot]');
        if (dot) self.goTo(Number(dot.getAttribute('data-p4-dot')));
      });
    }
    var engage = function () {
      self.engage();
    };
    ['pointerdown', 'touchstart', 'focusin'].forEach(function (type) {
      self.el.addEventListener(type, engage, { passive: true });
    });
    track.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'Home' && k !== 'End') return;
      e.preventDefault();
      self.engage();
      if (k === 'Home') self.goTo(0);
      else if (k === 'End') self.goTo(self.slides.length - 1);
      else self.step(k === 'ArrowRight' ? 1 : -1);
    });
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    // Drag / swipe (mouse + touch). touch-action: pan-y leaves vertical page scrolling to the browser.
    track.addEventListener('pointerdown', function (e) {
      if (self.slides.length < 2 || (e.pointerType === 'mouse' && e.button !== 0)) return;
      self.drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, x: e.clientX, t: e.timeStamp, vx: 0, p: 0, on: false };
    });
    track.addEventListener('pointermove', function (e) {
      var d = self.drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x0;
      var dy = e.clientY - d.y0;
      if (!d.on) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          self.drag = null;
          return;
        }
        if (Math.abs(dx) < 8) return;
        d.on = true;
        d.unit = self.unit();
        try {
          track.setPointerCapture(e.pointerId);
        } catch (err) {}
        self.engage();
        self.el.classList.add('is-dragging');
      }
      var dt = e.timeStamp - d.t;
      if (dt > 0) d.vx = 0.7 * ((e.clientX - d.x) / dt) + 0.3 * d.vx;
      d.x = e.clientX;
      d.t = e.timeStamp;
      d.p = Math.max(-1, Math.min(1, dx / d.unit));
      if (!self.raf) {
        self.raf = window.requestAnimationFrame(function () {
          self.raf = 0;
          self.drawDrag();
        });
      }
    });
    var end = function (e) {
      var d = self.drag;
      if (!d || e.pointerId !== d.id) return;
      self.drag = null;
      if (!d.on) return;
      self.suppressClick = true;
      setTimeout(function () {
        self.suppressClick = false;
      }, 0);
      if (self.raf) {
        window.cancelAnimationFrame(self.raf);
        self.raf = 0;
      }
      // Stale velocity (finger held still before lifting) does not count.
      var vx = e.timeStamp - d.t > 120 ? 0 : d.vx;
      var dir = 0;
      if (e.type !== 'pointercancel') {
        if (vx < -0.35 || (vx <= 0.35 && d.p <= -0.2)) dir = 1;
        else if (vx > 0.35 || d.p >= 0.2) dir = -1;
      }
      // Same frame: drop the inline drag pose and set the new classes, so the CSS transition runs from
      // the release position to the resting one.
      self.el.classList.remove('is-dragging');
      self.clearInline();
      if (dir) self.step(dir);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);

    // Horizontal trackpad swipe: one step per gesture (momentum events keep the lock alive).
    track.addEventListener(
      'wheel',
      function (e) {
        if (self.slides.length < 2 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        self.engage();
        var now = e.timeStamp;
        if (now < self.wheelLock) {
          self.wheelLock = now + 200;
          return;
        }
        self.wheelAcc = (now - self.wheelT > 200 ? 0 : self.wheelAcc) + e.deltaX;
        self.wheelT = now;
        if (Math.abs(self.wheelAcc) > 40) {
          self.step(self.wheelAcc > 0 ? 1 : -1);
          self.wheelAcc = 0;
          self.wheelLock = now + 500;
        }
      },
      { passive: false }
    );
  };

  // Centre-to-peek distance in px (--p4-g-dx is a literal vw value). Read once per drag.
  Gallery.prototype.unit = function () {
    var v = parseFloat(window.getComputedStyle(this.track).getPropertyValue('--p4-g-dx'));
    var px = v > 0 ? (v * window.innerWidth) / 100 : 0;
    return px || (this.slides[this.index] ? this.slides[this.index].offsetWidth * 0.7 : 200) || 200;
  };

  // Signed distance of slide i from the active one, in (-n/2, n/2]; ties go behind the travel direction.
  Gallery.prototype.offset = function (i, dir) {
    var n = this.slides.length;
    var o = (((i - this.index) % n) + n) % n;
    if (o > n / 2 || (o === n / 2 && dir > 0)) o -= n;
    return o;
  };

  Gallery.prototype.place = function (i, dir) {
    var n = this.slides.length;
    if (!n) return;
    this.index = ((i % n) + n) % n;
    for (var k = 0; k < n; k++) {
      var o = this.offset(k, dir);
      var c = o === 0 ? 'is-active' : o === -1 ? 'is-prev' : o === 1 ? 'is-next' : o < 0 ? 'is-before' : 'is-after';
      var cl = this.slides[k].classList;
      if (cl.contains(c)) continue;
      cl.remove.apply(cl, POS);
      cl.add(c);
    }
    this.setDots(this.index);
  };

  Gallery.prototype.step = function (d) {
    if (this.slides.length < 2) return;
    this.engage();
    this.place(this.index + d, d);
  };

  Gallery.prototype.goTo = function (i) {
    var n = this.slides.length;
    if (n < 2 || i === this.index) return;
    var o = this.offset(i, 0);
    this.engage();
    this.place(i, o > 0 ? 1 : -1);
  };

  // Drag pose: the same transform the classes use, at a fractional offset (p = -1..1 of a step).
  Gallery.prototype.drawDrag = function () {
    var d = this.drag;
    if (!d) return;
    // Ties (even n) go to the side the incoming slide enters from.
    var tie = d.p > 0 ? 1 : -1;
    for (var k = 0; k < this.slides.length; k++) {
      var raw = this.offset(k, tie);
      var s = this.slides[k].style;
      var f = this.slides[k].firstElementChild;
      // Slides beyond the +/-2 neighbours stay hidden at their resting (class) pose; only the +/-2 slide
      // on the side being revealed fades in, under the +/-1 cards (z-index never left to DOM order).
      if (Math.abs(raw) > 2) {
        s.transform = '';
        s.opacity = '0';
        s.zIndex = '0';
        if (f) f.style.borderRadius = '';
        continue;
      }
      var o = Math.max(-2, Math.min(2, raw + d.p));
      var a = Math.abs(o);
      var m = Math.min(1, a);
      s.transform =
        'translateX(calc(var(--p4-g-dx) * ' + o.toFixed(4) + ')) scale(calc(1 - (1 - var(--p4-g-k)) * ' + m.toFixed(4) + '))';
      s.opacity = String(Math.max(0, Math.min(1, 2 - a)));
      s.zIndex = Math.abs(raw) === 2 ? '1' : a < 0.5 ? '4' : a < 1.5 ? '3' : '2';
      if (f) f.style.borderRadius = 'calc(var(--p4-g-r0) + (var(--p4-g-r1) - var(--p4-g-r0)) * ' + m.toFixed(4) + ')';
    }
  };
  Gallery.prototype.clearInline = function () {
    this.slides.forEach(function (sl) {
      sl.style.transform = '';
      sl.style.opacity = '';
      sl.style.zIndex = '';
      if (sl.firstElementChild) sl.firstElementChild.style.borderRadius = '';
    });
  };

  Gallery.prototype.engage = function () {
    if (this.engaged) return;
    this.engaged = true;
    this.hydrateAround(this.index);
  };
  Gallery.prototype.hydrateAround = function (i) {
    var n = this.slides ? this.slides.length : 0;
    if (!n) return;
    for (var d = -2; d <= 2; d++) hydrateSlide(this.slides[(((i + d) % n) + n) % n]);
  };

  Gallery.prototype.collect = function () {
    this.slides = $$(this.track, '[data-p4-slide]');
    this.dots = this.dotsWrap ? $$(this.dotsWrap, '[data-p4-dot]') : [];
    this.el.classList.toggle('p4-gallery--single', this.slides.length < 2);
  };

  Gallery.prototype.setDots = function (i) {
    if (this.engaged) this.hydrateAround(i);
    this.dots.forEach(function (dot, n) {
      if (n === i) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  // Replace the slides with another design's images.
  Gallery.prototype.setMedia = function (media, label) {
    if (!this.track) return;
    var track = this.track;
    var id = track.id;
    this.drag = null;
    this.el.classList.remove('is-dragging');
    track.innerHTML = '';
    var count = media.length;
    media.forEach(function (m, n) {
      var slide = document.createElement('div');
      slide.className = 'p4-gallery__slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', n + 1 + ' / ' + count);
      slide.setAttribute('data-p4-slide', '');
      slide.setAttribute('data-media-id', m.id);
      var frame = document.createElement('div');
      frame.className = 'p4-gallery__frame';
      var img = document.createElement('img');
      img.className = 'p4-gallery__img';
      img.alt = m.alt || label || '';
      img.width = m.w || 1;
      img.height = m.h || 1;
      img.decoding = 'async';
      img.draggable = false;
      var srcset = IMG_WIDTHS.map(function (w) {
        return sizedUrl(m.src, w) + ' ' + w + 'w';
      }).join(', ');
      if (n === 0) {
        // The new active image: fetch it first.
        img.loading = 'eager';
        img.setAttribute('fetchpriority', 'high');
      } else {
        img.loading = 'lazy';
      }
      if (n < 2 || n === count - 1) {
        img.sizes = IMG_SIZES;
        img.srcset = srcset;
        img.src = sizedUrl(m.src, 1000);
      } else {
        img.setAttribute('data-srcset', srcset);
        img.setAttribute('data-src', sizedUrl(m.src, 1000));
      }
      frame.appendChild(img);
      slide.appendChild(frame);
      track.appendChild(slide);
    });
    track.setAttribute('aria-label', label || '');
    if (this.dotsWrap) {
      this.dotsWrap.innerHTML = '';
      if (count > 1) {
        for (var n = 0; n < count; n++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'p4-gallery__dot';
          // Pointer-only indicators: the track itself is the keyboard stop (arrow keys), so the dots stay out of the
          // tab order and the accessibility tree (also what keeps Lighthouse's touch-target audit off these 8 px dots).
          dot.tabIndex = -1;
          dot.setAttribute('aria-hidden', 'true');
          dot.setAttribute('data-p4-dot', n);
          dot.setAttribute('aria-controls', id);
          dot.setAttribute('aria-label', n + 1 + ' / ' + count);
          this.dotsWrap.appendChild(dot);
        }
      }
    }
    this.collect();
    this.place(0, 0);
  };

  /* ------------------------------------------------------------ Selling plans */

  var UNIT_DAYS = { day: 1, week: 7, month: 30, year: 365 };

  // "3-month", "Delivered every 3 months", "Every 2 weeks" -> { n: 3, unit: 'month' }
  function parseInterval(plan) {
    var texts = (plan.options || []).concat([plan.name || '', plan.group || '']);
    for (var i = 0; i < texts.length; i++) {
      var m = /(\d+)\s*-?\s*(day|week|month|year)s?\b/i.exec(String(texts[i]));
      if (m) return { n: Number(m[1]), unit: m[2].toLowerCase() };
    }
    return null;
  }
  function intervalText(iv) {
    return iv.n + ' ' + iv.unit + (iv.n === 1 ? '' : 's');
  }

  /* ------------------------------------------------------------ PDP */

  function Pdp(root) {
    this.root = root;
    var dataEl = $(root, '[data-p4-pdp-data]');
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    this.designs = this.data.designs || [];
    this.products = this.data.products || {};
    this.labels = this.data.labels || {};
    this.mode = this.data.mode === 'products' ? 'products' : 'variants';
    this.form = $(root, '[data-p4-form]');
    this.variantInput = $(root, '[data-p4-variant-id]');
    this.planInput = $(root, '[data-p4-selling-plan]');
    this.variantField = $(root, '[data-p4-variant-field]');
    this.variantSelect = $(root, '[data-p4-variant-select]');
    this.onetimeCard = $(root, '[data-p4-onetime-card]');
    this.onetimePrice = $(root, '[data-p4-onetime-price]');
    this.subCard = $(root, '[data-p4-sub-card]');
    this.subPrice = $(root, '[data-p4-sub-price]');
    this.subCompare = $(root, '[data-p4-sub-compare]');
    this.benefits = $$(root, '[data-p4-benefit]');
    this.radios = $$(root, '[data-p4-purchase]');
    this.qtyInput = $(root, '[data-p4-qty-input]');
    this.atc = $(root, '[data-p4-atc]');
    this.atcText = $(root, '[data-p4-atc-text]');
    this.error = $(root, '[data-p4-error]');
    this.status = $(root, '[data-p4-status]');
    this.rating = $(root, '[data-p4-rating]');
    this.sticky = $(root, '[data-p4-sticky]');
    this.stickyBtn = $(root, '[data-p4-sticky-btn]');
    this.stickyText = $(root, '[data-p4-sticky-text]');
    this.stickyCaption = $(root, '[data-p4-sticky-caption]');
    this.pending = false;

    var galleryEl = $(root, '[data-p4-gallery]');
    this.gallery = galleryEl ? new Gallery(galleryEl) : null;

    this.index = Math.max(0, Math.min(this.designs.length - 1, Number(this.data.activeIndex) || 0));
    this.purchase = 'onetime';
    this.planId = null;
    var d = this.design();
    this.variant = this.findVariant(Number(this.variantInput && this.variantInput.value)) ||
      this.findVariant(d && d.variantId) || this.defaultVariant();

    this.bind();
    this.initSticky();
    this.initPicker();
    this.initPlanPicker();
    this.renderVariantOptions();
    this.applyDefaultPurchase();
    this.render();
    this.renderRating();
  }

  Pdp.prototype.design = function () {
    return this.designs[this.index] || this.designs[0] || null;
  };
  Pdp.prototype.product = function () {
    var d = this.design();
    return (d && this.products[String(d.productId)]) || { variants: [], plans: [] };
  };
  // Variants the shopper can choose between for the current design.
  Pdp.prototype.designVariants = function () {
    var d = this.design();
    var vs = this.product().variants || [];
    if (this.mode === 'variants' && d) {
      return vs.filter(function (v) {
        return Number(v.id) === Number(d.variantId);
      });
    }
    return vs;
  };
  Pdp.prototype.findVariant = function (id) {
    var vs = this.designVariants();
    for (var i = 0; i < vs.length; i++) if (Number(vs[i].id) === Number(id)) return vs[i];
    return null;
  };
  Pdp.prototype.defaultVariant = function () {
    var vs = this.designVariants();
    for (var i = 0; i < vs.length; i++) if (vs[i].available) return vs[i];
    return vs[0] || null;
  };
  Pdp.prototype.allocationFor = function (planId) {
    if (!this.variant || !planId) return null;
    var al = this.variant.allocations || [];
    for (var i = 0; i < al.length; i++) if (Number(al[i].planId) === Number(planId)) return al[i];
    return null;
  };
  Pdp.prototype.allocation = function () {
    return this.allocationFor(this.planId);
  };

  /* One option per delivery interval (several app groups can offer the same interval). */
  Pdp.prototype.planOptions = function () {
    var self = this;
    var plans = this.product().plans || [];
    var tpl = String(this.data.planLabel || '');
    var seen = {};
    var out = [];
    plans.forEach(function (p) {
      var alloc = self.allocationFor(p.id);
      if (!alloc) return;
      var iv = parseInterval(p);
      var key = iv ? iv.n * UNIT_DAYS[iv.unit] : 'p' + p.id;
      if (seen[key]) return;
      seen[key] = true;
      var label = iv && tpl.indexOf('[interval]') !== -1 ? tpl.replace('[interval]', intervalText(iv)) : p.name;
      out.push({ id: Number(p.id), label: label, name: p.name || '', recurring: Number(p.recurring) || 0, sort: iv ? key : 1e9, alloc: alloc });
    });
    out.sort(function (a, b) {
      return a.sort - b.sort;
    });
    return out;
  };

  Pdp.prototype.bind = function () {
    var self = this;

    // Rating link -> reviews section: smooth scroll (owner, Oct 14), landing just below the sticky
    // announcement + header. One correction pass if something above the target changed height meanwhile.
    if (this.rating) {
      this.rating.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var id = (self.rating.getAttribute('href') || '').replace(/^#/, '');
        var target = id && document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var goal = function () {
          var chrome = document.querySelector('.p4-chrome-top');
          var offset = chrome ? Math.max(0, chrome.getBoundingClientRect().bottom) : 0;
          return Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - offset);
        };
        window.scrollTo({ top: goal(), behavior: reduce ? 'auto' : 'smooth' });
        var settled = false;
        var settle = function () {
          if (settled) return;
          settled = true;
          window.removeEventListener('scrollend', settle);
          var g = goal();
          if (Math.abs(g - window.pageYOffset) > 2) window.scrollTo({ top: g, behavior: 'auto' });
        };
        if ('onscrollend' in window) window.addEventListener('scrollend', settle);
        setTimeout(settle, reduce ? 50 : 1200);
        if (window.history && window.history.replaceState) window.history.replaceState(window.history.state, '', '#' + id);
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    }

    this.radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) {
          self.purchase = radio.value;
          self.render();
        }
      });
    });

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', function () {
        var v = self.findVariant(Number(self.variantSelect.value));
        if (!v) return;
        self.variant = v;
        self.render();
        self.updateUrl();
      });
    }

    $$(this.root, '[data-p4-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.setQty(self.qty() + Number(btn.getAttribute('data-p4-qty')));
      });
    });
    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', function () {
        self.setQty(self.qty());
      });
      this.qtyInput.addEventListener('input', function () {
        self.renderSticky();
      });
    }

    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        // Without the shared helpers, fall back to the native /cart/add form post.
        if (!hasP4()) return;
        e.preventDefault();
        self.add();
      });
    }
  };

  Pdp.prototype.setPurchase = function (value) {
    this.purchase = value;
    this.radios.forEach(function (r) {
      r.checked = r.value === value;
    });
    this.render();
  };

  Pdp.prototype.qty = function () {
    var n = parseInt(this.qtyInput && this.qtyInput.value, 10);
    return isNaN(n) ? 1 : n;
  };
  Pdp.prototype.setQty = function (n) {
    var max = Number(this.data.maxQuantity) || 10;
    n = Math.max(1, Math.min(max, n || 1));
    if (this.qtyInput) this.qtyInput.value = n;
    this.renderSticky();
  };

  /* ------------------------------------------------------------ Sticky Add to Bag */
  /* Fixed bar, mobile only (Oct 16 feedback #22: none on desktop; CSS hides it at min-width 769 and the class
     is never set there). Its button submits the same form. Shown via html.p4-sticky-atc-on (feedback #3):
       - on landing (scroll 0, also iOS bounce), even on tall screens where the label is already in view;
       - hidden from the moment the "Choose Your Designs" label is visible above the bar
       - until the reviews heading reaches the top area under the sticky header (only the section's own top
         padding left between them: where the rating link lands), and below that;
       - shown again when scrolling back up above the label.
     Without the reviews section: shown again once the main Add to Bag row has scrolled under the header.
     Positions are re-read on every (rAF-throttled) scroll / resize / pageshow / layout change, so Judge.me
     growing the reviews late and the iOS toolbar changing the viewport height are both covered.
     CSS still hides it while html.p4-modal-open / p4-cart-open / p4-sticky-hold. */
  Pdp.prototype.initSticky = function () {
    if (!this.sticky) return;
    var sticky = this.sticky;
    var root = this.root;
    var row = (this.atc && this.atc.closest('.p4-pdp__actions')) || this.atc;
    var html = document.documentElement;
    // Upsell -> cart drawer handoff: the popup drops p4-modal-open when it closes, but p4-cart.js adds
    // p4-cart-open only once its network refresh is back (~0.2 s, longer on a slow line), so the bar slid up
    // in between and vanished again. html.p4-sticky-hold keeps it hidden until the drawer is open (then
    // p4-cart-open hides it), or for 5 s if the drawer never opens.
    var holdTimer = null;
    var holdObs = null;
    var release = function () {
      clearTimeout(holdTimer);
      if (holdObs) holdObs.disconnect();
      holdObs = null;
      html.classList.remove('p4-sticky-hold');
    };
    document.addEventListener('p4:upsell', function (e) {
      var d = e.detail || {};
      if (d.action !== 'closed' || d.preview) return;
      release();
      html.classList.add('p4-sticky-hold');
      holdTimer = setTimeout(release, 5000);
      if ('MutationObserver' in window) {
        holdObs = new MutationObserver(function () {
          if (html.classList.contains('p4-cart-open')) release();
        });
        holdObs.observe(html, { attributes: true, attributeFilter: ['class'] });
      }
    });

    var desk = window.matchMedia ? window.matchMedia('(min-width: 769px)') : null;
    var label = $(root, '[data-p4-picker] .p4-pdp__label') || $(root, '.p4-pdp__buy .p4-pdp__label') || row;
    var reviews = null;
    var findReviews = function () {
      if (!reviews || !reviews.sec.isConnected) {
        var sec = document.querySelector('[data-p4-reviews]');
        reviews = sec ? { sec: sec, title: $(sec, '.p4-reviews__title') || sec } : null;
      }
      return reviews;
    };
    var raf = 0;
    var update = function () {
      raf = 0;
      if (desk && desk.matches) {
        html.classList.remove('p4-sticky-atc-on');
        return;
      }
      var on = true;
      var y = window.pageYOffset || html.scrollTop || 0;
      if (y > 0 && label) {
        var chrome = document.querySelector('.p4-chrome-top');
        var topEdge = chrome ? Math.max(0, chrome.getBoundingClientRect().bottom) : 0;
        var barTop = window.innerHeight - sticky.offsetHeight;
        var started = label.getBoundingClientRect().top < barTop;
        var ended = false;
        var r = findReviews();
        if (r) {
          var t = r.title.getBoundingClientRect().top;
          var lead = Math.max(0, t - r.sec.getBoundingClientRect().top);
          ended = t <= topEdge + lead + 1;
        } else if (row) {
          ended = row.getBoundingClientRect().bottom <= topEdge;
        }
        on = !started || ended;
      }
      html.classList.toggle('p4-sticky-atc-on', on);
    };
    var schedule = function () {
      if (!raf) raf = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('pageshow', schedule);
    window.addEventListener('load', schedule);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule, { passive: true });
    // Content growing / shrinking without a scroll (Judge.me, late images).
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(document.body || html);
    if (desk) {
      if (desk.addEventListener) desk.addEventListener('change', schedule);
      else if (desk.addListener) desk.addListener(schedule);
    }
    update();
  };
  Pdp.prototype.unitPrice = function () {
    var v = this.variant;
    if (!v) return 0;
    var alloc = this.purchase === 'subscription' ? this.allocation() : null;
    return alloc ? alloc.price : v.price;
  };
  Pdp.prototype.renderSticky = function () {
    if (!this.sticky) return;
    if (this.stickyCaption) {
      var tpl = this.stickyCaption.getAttribute('data-template') || '';
      this.stickyCaption.textContent = tpl.replace('[price]', money(this.unitPrice() * this.qty()));
    }
    if (this.stickyBtn && this.atc) {
      this.stickyBtn.disabled = this.atc.disabled;
      if (this.atc.getAttribute('aria-busy') === 'true') this.stickyBtn.setAttribute('aria-busy', 'true');
      else this.stickyBtn.removeAttribute('aria-busy');
    }
    if (this.stickyText && this.atcText) this.stickyText.textContent = this.atcText.textContent;
  };

  /* ------------------------------------------------------------ Listbox pickers (design + interval) */
  /* Shared button + role=listbox behaviour (keyboard: arrows, Home/End, PageUp/Down, type-ahead, Enter / Space,
     Esc, Tab; outside pointer closes). The open list overlays the toggle inside the same rounded box and its
     first row is the selected item at the toggle's exact spot (callers keep it first), so nothing moves on
     open / close. opts.onOpen(via: 'click' | 'key'), opts.onChoose(row). Rows are read in DOM order on open. */
  function listbox(picker, opts) {
    var toggle = $(picker, '[data-p4-picker-toggle]');
    var list = $(picker, '[data-p4-listbox]');
    if (!toggle || !list) return null;
    var rows = [];
    var active = 0;
    var typed = '';
    var typedTimer = null;

    function highlight(p, scroll) {
      if (!rows.length) return;
      active = Math.max(0, Math.min(rows.length - 1, p));
      rows.forEach(function (o, n) {
        o.classList.toggle('is-active', n === active);
      });
      list.setAttribute('aria-activedescendant', rows[active].id);
      if (scroll) {
        var o = rows[active];
        if (o.offsetTop < list.scrollTop) list.scrollTop = o.offsetTop;
        else if (o.offsetTop + o.offsetHeight > list.scrollTop + list.clientHeight) {
          list.scrollTop = o.offsetTop + o.offsetHeight - list.clientHeight;
        }
      }
    }
    function open(via) {
      if (opts.onOpen) opts.onOpen(via);
      rows = $$(list, '[role="option"]');
      list.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      picker.classList.add('is-open');
      list.scrollTop = 0;
      highlight(0, false);
      // preventScroll: a plain focus() scrolled the page when the open box ran past the viewport bottom
      // (47 px at 1630 x 560), which moved the whole picker under the pointer.
      try {
        list.focus({ preventScroll: true });
      } catch (e) {
        list.focus();
      }
      document.addEventListener('pointerdown', onDocDown, true);
    }
    function close(returnFocus) {
      // Focus back first, then hide: hiding the focused list first dropped focus to <body> for a moment.
      // After a click / tap, focus returns without a keyboard ring (Chrome otherwise shows one on this script
      // focus, and the next pointer open then ringed the first row); .is-pointer covers browsers without it.
      if (returnFocus) {
        if (picker.classList.contains('is-pointer')) {
          try {
            toggle.focus({ preventScroll: true, focusVisible: false });
          } catch (e) {
            toggle.focus();
          }
        } else {
          toggle.focus();
        }
      }
      list.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      picker.classList.remove('is-open');
      document.removeEventListener('pointerdown', onDocDown, true);
    }
    function onDocDown(e) {
      if (!picker.contains(e.target)) close(false);
    }
    function choose(row) {
      close(true);
      opts.onChoose(row);
    }

    // Input modality: a click / tap on the picker marks it .is-pointer (no keyboard rings: see close() and
    // the CSS); any key press anywhere clears it, so keyboard users always get the rings.
    picker.addEventListener(
      'pointerdown',
      function () {
        picker.classList.add('is-pointer');
      },
      { passive: true }
    );
    document.addEventListener(
      'keydown',
      function () {
        picker.classList.remove('is-pointer');
      },
      true
    );
    toggle.addEventListener('click', function () {
      if (list.hidden) open('click');
      else close(true);
    });
    toggle.addEventListener('keydown', function (e) {
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && list.hidden) {
        e.preventDefault();
        open('key');
      }
    });
    list.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowDown' || k === 'ArrowUp') {
        e.preventDefault();
        highlight(active + (k === 'ArrowDown' ? 1 : -1), true);
      } else if (k === 'Home' || k === 'End') {
        e.preventDefault();
        highlight(k === 'Home' ? 0 : rows.length - 1, true);
      } else if (k === 'PageDown' || k === 'PageUp') {
        e.preventDefault();
        highlight(active + (k === 'PageDown' ? 6 : -6), true);
      } else if (k === 'Enter' || (k === ' ' && !typed)) {
        e.preventDefault();
        if (rows[active]) choose(rows[active]);
      } else if (k === 'Escape') {
        e.preventDefault();
        close(true);
      } else if (k === 'Tab') {
        close(false);
      } else if (k.length === 1 && (/\S/.test(k) || typed)) {
        typed += k.toLowerCase();
        clearTimeout(typedTimer);
        typedTimer = setTimeout(function () {
          typed = '';
        }, 600);
        for (var n = 0; n < rows.length; n++) {
          var idx = (active + (typed.length === 1 ? 1 : 0) + n) % rows.length;
          var name = (rows[idx].textContent || '').trim().toLowerCase();
          if (name.indexOf(typed) === 0) {
            highlight(idx, true);
            break;
          }
        }
      }
    });
    list.addEventListener('click', function (e) {
      var o = e.target.closest && e.target.closest('[role="option"]');
      // The open box covers the toggle; a click beside the rows (the chevron area) closes it.
      if (o && list.contains(o)) choose(o);
      else close(true);
    });
    list.addEventListener('pointermove', function (e) {
      var o = e.target.closest && e.target.closest('[role="option"]');
      if (o) highlight(rows.indexOf(o), false);
    });
    return { el: picker, toggle: toggle, list: list, close: close };
  }

  // Keep `first` as the first row, the others in `order`; touches the DOM only when the order changed.
  function orderRows(list, order, first) {
    var want = first ? [first].concat(order.filter(function (o) {
      return o !== first;
    })) : order.slice();
    var dom = $$(list, '[role="option"]');
    var same = dom.length === want.length && want.every(function (o, n) {
      return dom[n] === o;
    });
    if (!same) {
      want.forEach(function (o) {
        list.appendChild(o);
      });
    }
    return want;
  }

  /* Design picker. options[i] = design i (design order); on screen the selected design is the first row. */
  Pdp.prototype.initPicker = function () {
    var self = this;
    var picker = $(this.root, '[data-p4-picker]');
    if (!picker) return;
    var designOf = function (o) {
      return Number(o.getAttribute('data-p4-option'));
    };
    var lb = listbox(picker, {
      onOpen: warm,
      onChoose: function (row) {
        self.selectDesign(designOf(row));
      }
    });
    if (!lb) return;
    var list = lb.list;
    var byDesign = $$(list, '[data-p4-option]').sort(function (a, b) {
      return designOf(a) - designOf(b);
    });
    function reorder() {
      orderRows(list, byDesign, byDesign[self.index]);
    }
    reorder();
    this.picker = { el: picker, toggle: lb.toggle, list: list, options: byDesign, reorder: reorder };

    // Option swatches are not fetched on page view (the list is hidden); load them on intent so the
    // list opens complete.
    function warm() {
      $$(list, 'img[data-src]').forEach(function (img) {
        img.src = img.getAttribute('data-src');
        img.removeAttribute('data-src');
      });
    }
    ['pointerenter', 'touchstart', 'focus'].forEach(function (type) {
      lb.toggle.addEventListener(type, warm, { once: true, passive: true });
    });
  };

  /* Interval picker (Oct 16 feedback #7, replaces the native <select>). Rows are rendered by
     snippets/p4-pdp-frequency.liquid and rebuilt by renderPlanPicker() when the design's plans differ.
     Opening it by click / tap selects Auto-Refill (as the select's mousedown did); choosing a plan does too. */
  Pdp.prototype.initPlanPicker = function () {
    var self = this;
    var picker = $(this.root, '[data-p4-plan-picker]');
    if (!picker) return;
    this.planPicker = listbox(picker, {
      onOpen: function (via) {
        if (via === 'click' && self.purchase !== 'subscription') self.setPurchase('subscription');
      },
      onChoose: function (row) {
        self.planId = Number(row.getAttribute('data-p4-plan-option')) || null;
        self.setPurchase('subscription');
      }
    });
  };

  Pdp.prototype.renderPlanPicker = function (plans) {
    var pp = this.planPicker;
    if (!pp) return;
    var list = pp.list;
    var sid = this.root.getAttribute('data-section-id') || '';
    var key = plans
      .map(function (p) {
        return p.id;
      })
      .join(',');
    if (list.getAttribute('data-key') !== key) {
      list.innerHTML = '';
      plans.forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'p4-pdp__option';
        li.setAttribute('role', 'option');
        li.id = 'P4PlanOpt-' + sid + '-' + p.id;
        li.setAttribute('data-p4-plan-option', p.id);
        var name = document.createElement('span');
        name.className = 'p4-pdp__option-name';
        name.textContent = p.label;
        li.appendChild(name);
        list.appendChild(li);
      });
      list.setAttribute('data-key', key);
    }
    var current = null;
    var label = '';
    var byPlan = plans.map(function (p) {
      var o = $(list, '[data-p4-plan-option="' + p.id + '"]');
      if (o && p.id === this.planId) {
        current = o;
        label = p.label;
      }
      return o;
    }, this).filter(Boolean);
    byPlan.forEach(function (o) {
      o.setAttribute('aria-selected', o === current ? 'true' : 'false');
    });
    orderRows(list, byPlan, current);
    var nameEl = $(pp.toggle, '[data-p4-current-name]');
    if (nameEl && nameEl.textContent !== label) nameEl.textContent = label;
  };

  Pdp.prototype.selectDesign = function (i) {
    if (!this.designs[i] || i === this.index) return;
    this.index = i;
    var design = this.design();

    if (this.gallery) this.gallery.setMedia(design.media || [], design.label);

    if (this.picker) {
      this.picker.options.forEach(function (o, n) {
        o.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
      // The list is closed here: move the new selection to the first row for the next open.
      this.picker.reorder();
      var name = $(this.picker.toggle, '[data-p4-current-name]');
      if (name) name.textContent = design.label;
      var badge = $(this.picker.toggle, '[data-p4-current-badge]');
      if (badge) badge.hidden = !design.badge;
      var sw = $(this.picker.toggle, '[data-p4-current-swatch]');
      if (sw) {
        // 4-circle strip when the design has one (feedback #5), else the single round swatch.
        var strip = design.strip || '';
        sw.innerHTML = '';
        sw.classList.toggle('p4-pdp__swatch--strip', !!strip);
        sw.classList.toggle('p4-pdp__swatch--fallback', !strip && !!design.swatchFallback);
        if (strip || design.swatch) {
          var img = document.createElement('img');
          img.src = strip || design.swatch;
          img.alt = '';
          img.width = strip ? 486 : 60;
          img.height = strip ? 109 : 60;
          sw.appendChild(img);
        }
      }
    }

    this.variant = this.findVariant(design.variantId) || this.defaultVariant();
    this.renderVariantOptions();
    this.applyDefaultPurchase(true);
    this.render();
    this.renderRating();
    this.updateUrl();
    this.announce(design.label);
    this.hideError();
    // The quick-add section below reorders itself (p4-pdp-extras.js initDesignOrder).
    document.dispatchEvent(
      new CustomEvent('p4:design-change', {
        detail: { variantId: Number(design.variantId), productId: Number(design.productId) }
      })
    );
  };

  Pdp.prototype.renderVariantOptions = function () {
    if (!this.variantSelect || !this.variantField) return;
    var vs = this.mode === 'products' ? this.designVariants() : [];
    this.variantField.hidden = vs.length < 2;
    var self = this;
    this.variantSelect.innerHTML = '';
    vs.forEach(function (v) {
      var opt = document.createElement('option');
      opt.value = v.id;
      opt.textContent = v.title + (v.available ? '' : ' – ' + (self.labels.soldOut || 'Sold out'));
      if (self.variant && Number(v.id) === Number(self.variant.id)) opt.selected = true;
      self.variantSelect.appendChild(opt);
    });
  };

  Pdp.prototype.applyDefaultPurchase = function (keepPurchase) {
    var plans = this.planOptions();
    var match = String(this.data.defaultPlanMatch || '').toLowerCase().trim();
    var current = this.planId;
    var chosen = null;
    // Keep the shopper's interval when switching designs, if it exists for the new one.
    if (keepPurchase && current) {
      for (var c = 0; c < plans.length; c++) if (plans[c].id === current) chosen = plans[c];
    }
    if (!chosen && match) {
      for (var i = 0; i < plans.length; i++) {
        if ((plans[i].label + ' ' + plans[i].name).toLowerCase().indexOf(match) !== -1) {
          chosen = plans[i];
          break;
        }
      }
    }
    if (!chosen) chosen = plans[0] || null;
    this.planId = chosen ? chosen.id : null;

    var purchase = keepPurchase ? this.purchase : this.data.defaultPurchase === 'subscription' ? 'subscription' : 'onetime';
    if (!plans.length) purchase = 'onetime';
    if (this.product().requiresSellingPlan && plans.length) purchase = 'subscription';
    this.setPurchaseSilently(purchase);
  };

  Pdp.prototype.setPurchaseSilently = function (value) {
    this.purchase = value;
    this.radios.forEach(function (r) {
      r.checked = r.value === value;
    });
  };

  Pdp.prototype.render = function () {
    var v = this.variant;
    var plans = this.planOptions();
    var requires = !!this.product().requiresSellingPlan;
    var plan = null;
    for (var i = 0; i < plans.length; i++) if (plans[i].id === this.planId) plan = plans[i];
    if (!plan && plans.length) {
      plan = plans[0];
      this.planId = plan.id;
    }
    if (!plans.length) this.planId = null;
    if (!plans.length && this.purchase === 'subscription') this.setPurchaseSilently('onetime');

    if (this.variantInput) this.variantInput.value = v ? v.id : '';

    if (this.onetimeCard) {
      this.onetimeCard.hidden = requires && plans.length > 0;
      this.onetimeCard.classList.toggle('is-selected', this.purchase === 'onetime');
    }
    if (this.onetimePrice) this.onetimePrice.textContent = v ? money(v.price) : '';

    if (this.subCard) {
      this.subCard.hidden = plans.length === 0;
      this.subCard.classList.toggle('is-selected', this.purchase === 'subscription');
    }
    this.renderPlanPicker(plans);

    if (plan && v) {
      var alloc = plan.alloc;
      var compare = alloc.compareAtPrice && alloc.compareAtPrice > alloc.price ? alloc.compareAtPrice : v.price;
      if (this.subPrice) this.subPrice.textContent = money(alloc.price);
      if (this.subCompare) {
        this.subCompare.textContent = compare > alloc.price ? money(compare) : '';
        this.subCompare.hidden = !(compare > alloc.price);
      }
      // Truthful "Save X%" copy (same rules as the Liquid in sections/p4-pdp-main.liquid):
      //   first = first-order saving (allocation price vs compare price), recurring = the plan's last
      //   price adjustment (the refills). Intro template when the first order is discounted more.
      var round1 = function (n) {
        return Math.round(n * 10) / 10;
      };
      var pctLabel = function (n) {
        return (n % 1 === 0 ? n.toFixed(0) : String(n)) + '%';
      };
      var first = compare > 0 ? round1(((compare - alloc.price) / compare) * 100) : 0;
      var recurring = round1(Number(plan.recurring) || 0);
      var pct = recurring > 0 ? recurring : first;
      this.benefits.forEach(function (li) {
        var intro = li.getAttribute('data-template-intro') || '';
        var tpl = li.getAttribute('data-template') || '';
        if (intro && recurring > 0 && first > recurring) tpl = intro;
        if (tpl.indexOf('percent]') === -1) return;
        li.hidden = pct <= 0;
        var text = $(li, '[data-p4-benefit-text]') || li;
        text.textContent = tpl.replace('[first_percent]', pctLabel(first)).replace('[percent]', pctLabel(pct));
      });
    }

    if (this.planInput) {
      var useSub = this.purchase === 'subscription' && this.planId;
      this.planInput.disabled = !useSub;
      this.planInput.value = useSub ? this.planId : '';
    }

    if (this.atc && !this.pending) {
      var available = !!(v && v.available);
      this.atc.disabled = !available;
      if (this.atcText) {
        this.atcText.textContent = !v ? this.labels.unavailable : available ? this.labels.addToCart : this.labels.soldOut;
      }
    }
    this.renderSticky();
  };

  Pdp.prototype.renderRating = function () {
    if (!this.rating || this.mode !== 'products') return;
    var src = this.data.ratingSource;
    var d = this.design() || {};
    var shop = this.data.shopRating || {};
    var ref = this.data.referenceRating || {};
    var rating = 0;
    var count = 0;
    // Same resolution as the Liquid in sections/p4-pdp-main.liquid (server-rendered first paint).
    if ((src === 'product' || src === 'product_then_shop') && Number(d.ratingCount) > 0) {
      rating = Number(d.rating);
      count = Number(d.ratingCount);
    } else if ((src === 'reference' || src === 'reference_then_shop') && Number(ref.count) > 0) {
      rating = Number(ref.rating);
      count = Number(ref.count);
    } else if ((src === 'shop' || /_then_shop$/.test(src)) && Number(shop.count) > 0) {
      rating = Number(shop.rating);
      count = Number(shop.count);
    }
    if (!count) {
      this.rating.hidden = true;
      return;
    }
    this.rating.hidden = false;
    var stars = $(this.rating, '.p4-stars');
    if (stars) stars.style.setProperty('--p4-stars-fill', Math.min(100, (rating / 5) * 100) + '%');
    var countEl = $(this.rating, '[data-p4-rating-count]');
    if (countEl) countEl.textContent = '(' + count.toLocaleString('en-US') + ')';
    var label = $(this.rating, '[data-p4-rating-label]');
    if (label) {
      label.textContent = String(this.labels.reviews || '')
        .replace('[rating]', String(Math.round(rating * 100) / 100))
        .replace('[count]', count.toLocaleString('en-US'));
    }
  };

  Pdp.prototype.updateUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var design = this.design();
    if (!design) return;
    try {
      var base = this.mode === 'products' ? design.url : window.location.pathname;
      var url = new URL(base, window.location.origin);
      var current = new URLSearchParams(window.location.search);
      current.forEach(function (value, key) {
        if (key !== 'variant') url.searchParams.set(key, value);
      });
      var multi = this.mode === 'variants' || this.designVariants().length > 1;
      if (this.variant && multi) url.searchParams.set('variant', this.variant.id);
      else url.searchParams.delete('variant');
      url.hash = window.location.hash;
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) {
      /* ignore */
    }
  };

  Pdp.prototype.announce = function (text) {
    if (!this.status) return;
    this.status.textContent = '';
    var el = this.status;
    window.setTimeout(function () {
      el.textContent = text;
    }, 50);
  };
  Pdp.prototype.showError = function (msg) {
    if (!this.error) return;
    this.error.textContent = msg || this.labels.error;
    this.error.hidden = false;
  };
  Pdp.prototype.hideError = function () {
    if (!this.error) return;
    this.error.hidden = true;
    this.error.textContent = '';
  };

  Pdp.prototype.add = function () {
    if (this.pending) return;
    var v = this.variant;
    if (!v || !v.available) return;
    var self = this;
    var design = this.design();
    this.setQty(this.qty());
    var qty = this.qty();
    var sellingPlanId = this.purchase === 'subscription' && this.allocation() ? Number(this.planId) : null;
    var item = { id: Number(v.id), quantity: qty };
    if (sellingPlanId) item.selling_plan = sellingPlanId;
    // "Includes" line item property, like the classic product forms (snippets/p4-includes.liquid)
    var includes = v.includes || (this.product() || {}).includes;
    if (includes) item.properties = { Includes: includes };

    this.pending = true;
    this.hideError();
    this.atc.disabled = true;
    this.atc.setAttribute('aria-busy', 'true');
    if (this.atcText) this.atcText.textContent = this.labels.adding;
    this.renderSticky();

    window.P4.addItems([item])
      .then(function (response) {
        self.announce(self.labels.added);
        // Only 4-packs trigger the upsell (contract); anything else just refreshes the drawer.
        if (!window.P4.isPackProduct(design.productId)) {
          window.P4.refreshCartDrawer({ open: true });
          return;
        }
        window.P4.packAdded({
          productId: Number(design.productId),
          variantId: Number(v.id),
          quantity: qty,
          sellingPlanId: sellingPlanId,
          source: 'pdp',
          response: response
        });
      })
      .catch(function (err) {
        self.showError(err && err.message);
      })
      .then(function () {
        self.pending = false;
        self.atc.removeAttribute('aria-busy');
        self.render();
      });
  };

  /* ------------------------------------------------------------ Boot */

  function boot(scope) {
    scope = scope || document;
    $$(scope, '[data-p4-pdp]').forEach(function (el) {
      if (el.__p4Pdp) return;
      el.__p4Pdp = new Pdp(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else boot();

  // Theme editor: re-init a section after it is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
